/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cdmiller
 */

// import math from 'mathjs';
// import {Dyn} from './dynMiscellany';
// import mediator from './mediatorUtil';

export const wrapDynDataSet = function(Dyn) {
    let mediator = Dyn.mediator;

    /** helper function for DataSet to notify views/panels about how attributes have changed. */
    class AttributeNotifier {
        constructor() {
            this.attributeIDs = [];  // specified attributes have changed, else assume all attributes have changed.
        }
        isAllAttributeChange() {
            // if we don't specify any attributes, caller must assume all attributes have changed
            return this.attributeIDs.length === 0;
        }
        isAnythingChanged() { // NOTE: just check flags, use isAllAttributeChange() for which attributes have changed
            for( let prop in this ) {
                if( this.hasOwnProperty(prop) && prop !== 'attributeIDs' && this[prop]) {
                    return true;
                }
            }
            return false; // none of our flags has been set to true
        }
        /**
         * Set the flags for what has changed, and optional set of attribute IDs that have changed.
         * Unknown properties, not found in constructor, are rejected.
         * @param args
         */
        updateNotifier( args ) {
            for( let prop in args ) {   // check to make sure all properties are standard ones
                if( args.hasOwnProperty( prop ) && this.hasOwnProperty( prop )) {
                    if( prop === 'attributeIDs') {
                        // add ids not already in our list of changed attributes
                        this.attributeIDs = this.attributeIDs.concat( args.attributeIDs.filter( id => ! this.attributeIDs.includes(id) ));
                    } else {
                        // update one of our constructed properties such as this.formulaUpdate = true
                        this[ prop ] = args[ prop ];
                        Dyn.assert( typeof this[prop] === 'boolean');
                    }
                } else {
                    Dyn.assert( false, "invalid parameter: "+prop );
                }
            }
            return this; // for chaining
        }
    }

    /** helper class to notify views about attribute value changes in the dataset (e.g. edits in case table) */
    class AttributeValuesNotifier extends AttributeNotifier {
        // Construct args for the "records:setAttributeValues" notification
        // which is used when dataSet attributes or case data or attribute metadata is added/deleted/updated
        constructor( optionalFlags ) {
            super();
            // define "what changed" to send to subscribers; e.g. did the attribute name change?
            this.nameUpdate = false;        // true if 1+ attribute names have changed
            this.descriptionUpdate = false; // true if 1+ description names have changed
            this.formulaUpdate = false;     // true if 1+ formula strings added/deleted/updated
            this.caseValueUpdate = false;   // true if 1+ case values have changed
            this.numCasesUpdate = false;    // true if dataSet.getNumCasesPreFilter() value has changed.
            this.addAttribute = false;      // true if 1+ attributes added to the dataSet
            this.deleteAttribute = false;   // true if 1+ attributes removed from the dataSet
            this.isIgnoredEdit = false; // true if case value change should be ignored and reverted (e.g. 1 => 1.0)
            this.reEvalAttribIDs = []; // ids of attributes with formulas that need re-evaluation
            // for now don't record which cases, but we can add this.caseIndices = [] if needed for efficiency
            // optionally set any of the above flags
            if( optionalFlags ) {
                this.updateNotifier( optionalFlags );
            }
        }
        notify() {
            Dyn.assert( this.isAnythingChanged()); // don't call if nothing has changed
            mediator.publish( "records:setAttributeValues", this );
        }
    }

    class AttributeMetadataNotifier extends AttributeNotifier {
        constructor( optionalFlags ) {
            super();
            // define allowable flags for indicating which attribute metadata has changed
            this.dataTypeUpdate = false;
            this.sortOrderUpdate = false;
            this.categoryOrderUpdate = false;
            this.numericDecimalsUpdate = false;
            this.numericIntervalsUpdate = false;
            // optionally set any of the above flags
            if( optionalFlags ) {
                this.updateNotifier( optionalFlags );
            }
        }
        notify() {
            Dyn.assert( this.attributeIDs.length===1 );
            Dyn.assert( this.isAnythingChanged()); // don't call if nothing has changed
            mediator.publish( "records:setAttributeMetadata", this );
        }
        get attribute() {
            Dyn.assert( this.attributeIDs.length===1 );
            return Dyn.dataSet.getAttributeByID( this.attributeIDs[0] );
        }
    }

    /** Class to hold and change DataSet metadata */
    class MetaData {
        constructor( rawMetaData ) {
            this.fields = [];
            Object.assign( this, rawMetaData ); // merge metaData into new object
        }

        /**
         * create a new attribute suitable for adding to the dataset, e.g. a formula attribute
         * to be calculated by the caller.
         * @returns {Dyn.Attribute}
         */
        static newAttributeFromMetaData( id, name, cases, metaData ) {
            let attributeIndex = 0, // caller should update later
                attributeCount = 1; // caller should update later
            return new Dyn.Attribute( name, cases, attributeIndex, attributeCount, id, metaData );
        }

        static newMetaDataFieldFromName( attribIndex, name, longName, formulaString ) {
            // consistently construct a metaDataField for a dataSet, with optional parameters
            let attribName = name || 'attrib',
                attribId = Dyn.plotUtil.slugify( attribName ) + "-attrib" + attribIndex,
                metaData = {
                    "sort": "ascending",
                    "name": attribName,
                    "long_name": longName || attribName, // field.long_name maps to attribute.longName
                    "id": attribId
                };
            if( formulaString ) {
                metaData.attribute_type = "derived";
                metaData.formula = formulaString;
            }
            return metaData;
        }
    } // class MetaData
    Dyn.MetaData = MetaData;

    class DataSet extends Dyn.UndoHelper {

        /**
         * Construct a dataSet for use by the plot/table/data-cards
         * All table data is stored in DataSet.attribs, one per column of the table.
         * @param title {string} -- dataset title string
         * @param columnIds {array} -- array of attribute names adjusted to be unique within its Dyn.dataSet
         * @param columnNames {array} -- array of attribute names for user display
         * @param tableRows {*}
         * @param metaData {*} -- optional table and attribute parameters to override default behavior.
         * @param dataSource
         * @param dataUrl
         * @constructor
         */
        constructor(title, columnIds, columnNames, tableRows, metaData, dataSource, dataUrl, gettextCatalog) {
            super();
            //Dyn.time("Dataset:init", "Start");
            Dyn.dataSet = this;

            this.gettextCatalog = gettextCatalog;
            this.title = title;
            this.columnIds = columnIds;
            this.columnNames = columnNames;
            this.metaData = new MetaData(metaData);
            this.attribs = null;
            this.selectedAttributeAlsoOrders = true;  // mode; use false to change behavior of setSelectedAttribute()
            this.orderByAttribute = -1;   //  1+ attribs/columnIds index, or -1 for natural case order (0===case attribute, not used)
            this.selectedAttribute = -1;  //  1+ attribs/columnIds index, or -1 for nothing selected (0===case attribute, not used)
            this.selectedCategories = []; // array of selected category names, 0+, only if used to select cases.
            this.selectedCategoryAttribute = null; // selected category's attribute
            this.selectedNumericRange = null; // data range used to select cases, if any. Type {Dyn.mathUtil.Range | null}
            this.selectedCases = [];	// array of numeric case indexes, empty if no cases are selected
            this.caseFilter = new Dyn.CaseFilter(this);
            this.missingCases = new Dyn.MissingCaseManager(this);
            this.caseSampler = new Dyn.CaseSampler(this);
            this.dataSource = dataSource;
            this.dataUrl = dataUrl || "tuvalabs.com";

            this.init(tableRows);
            this.checkData();

            // To restore filters
            if( metaData && metaData.filters ){
                this.caseFilter.restore(metaData.filters);
            }
            //Dyn.time("Dataset:init", "End");
        }

        checkData() {
            // check consistency of data
            // TODO: consider getting rid of these parameters: columnIds, columnNames, metaData.
            // Because all of this data is redundant with .attribs, and attribs will change with formula editing, value editing, etc. -CDM 2017-03-31
            Dyn.assert( this.metaData && this.metaData.fields );
            if( this.metaData && this.metaData.fields ) {
                Dyn.assert( this.metaData.fields.length === this.columnIds.length, "metaData.length does not match columnIds.length" );
                Dyn.assert( this.metaData.fields.length === this.columnNames.length, "metaData.length does not match columnNames.length" );
                Dyn.assert( this.metaData.fields.length === this.attribs.length, "metaData.length does not match attribs.length" );
            }
            this.attribs.forEach( (a,i) => {
                if(this.metaData) {
                    Dyn.assert( a.id === this.metaData.fields[i]?.id, "metaData.fields[].id does not match attribs[].id" );
                }
                Dyn.assert( a.id === this.columnIds[i], "metaData.fields[].id does not match columnIds[]");
            });
        }

        init(tableRows) {
            var self = this;

            // create the case-attribute data
            let tableColumns = Dyn.dataUtil.copyRecordListToColumnArrays( tableRows, this.columnIds );
            this.attribs = Dyn.dataUtil.copyColumnArraysToAttribs(this.columnNames, tableColumns, this.columnIds, this.metaData);
            this.evaluateFormulas(); // do only after all non-formula attributes are initialized
            this.updateAllAttributeColors();
            if( this.attribs ){
                // turn off animation by default if loading a very large dataset.  User can turn it on later from menu.
                let isSmallDataSetAndCanAnimateByDefault = (this.getNumCasesPreFilter() <= Dyn.plotUtil.maxNumIconsToAnimate);
                // if animation is explicitly marked as off, don't bother to select automatically on behalf of user
                if (Dyn.preferences.isAnimationOn() !== false){
                    Dyn.preferences.setAnimationOn(isSmallDataSetAndCanAnimateByDefault);
                }
            }
            mediator.publish("records:initialized", this);

            // Selection API
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:attributeNameSelected", function(selectData) {
                    self.setSelectedAttribute(selectData);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:casesSelected", (data) => this.setSelectedCaseByIndex(data)));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:casesSelectedByCategory", function(selectData) {
                    self.setSelectedCaseByCategory(selectData);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:casesSelectedByNumericRange", function(selectData) {
                    self.setSelectedCaseByNumericRange(selectData);
                }));

            // attribute API
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:attributeDatatypeUpdated", function(attributeData) {
                    self.updateAttributeDataType(attributeData);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:attributeSortOrderUpdated", function(attributeData) {
                    self.updateAttributeSortOrder(attributeData);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:attributeNumericDecimalsUpdated", function(attributeData) {
                    self.updateAttributeNumericDecimals(attributeData);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:attributeNumericIntervalsUpdated", function(attributeData) {
                    self.updateAttributeNumericIntervals(attributeData);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("records:updateAttributesDisplay", function(attributeList) {
                    self.updateAttributesDisplay(attributeList);
                }));

            // Case Filter API
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:caseFilterReset", function(filterdata) {
                    self.caseFilter.resetAll(filterdata);
                }));
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:caseFilterExcludeSelected", function(filterData) {
                    self.caseFilter.excludeSelected(filterData);
                }));
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:caseFilterKeepOnlySelected", function(filterData) {
                    self.caseFilter.keepOnlySelected(filterData);
                }));
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:caseFilterByAttribute", function(filterData) {
                    self.caseFilter.filterByAttribute(filterData);
                }));
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:generateRandomSamples", function(filterData) {
                    self.caseSampler.generateRandomSamples(filterData);
                }));
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:viewSample", function(samplerData) {
                    self.caseSampler.viewSample(samplerData);
                })); // { sampleID:"id" || sampleIndex:}
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:viewDistribution", function(samplerData) {
                    self.caseSampler.viewDistribution(samplerData);
                })); // {}
            Dyn.addSubcribersToClean(
                mediator.subscribe("records:viewPopulation", function(samplerData) {
                    self.caseSampler.viewPopulation(samplerData);
                })); // {}
            Dyn.addSubcribersToClean(
                mediator.subscribe("sampleStats:updated", function(args) {
                    self.caseSampler.updateDistribution(args);
                }));
            Dyn.addSubcribersToClean(
                mediator.subscribe("toolBar:orderByAttribute", function(orderByData) {
                    self.setOrderByAttribute(orderByData);
                }));

            // colorUtil API
            Dyn.addSubcribersToClean(
                mediator.subscribe("colorUtil:newAttributeColors", (data) => {
                    this.updateAllAttributeColors(data);
                }));

            Dyn.addSubcribersToClean(
                mediator.subscribe("moveCategoryOfAttribute", (data) => {
                    this.moveCategoryOfAttribute(data);
                }));
            // Add Case-Attribute Values API?
            //records:setAttributeValues { attribute: Dyn.Attribute }
            //records:setCaseValues { caseIndex: n }
            //records:setCaseAttributeValue { { attribute: Dyn.Attribute, caseIndex: n }
            //records:setAllCaseAttributeValues { }

            DataSet.unitTest( this );
        }

        /**
         * Do any initialization after the visualization has been set up.
         * (e.g. plots, toolbars, case cards, table are initialized).
         */
        initPostVisual() {
            Dyn.undoManager.resetAll(); // clear the undo stack and update the undo/redo buttons
        }

        /** save data for restore(), making sure to copy not use references. */
        save() {
            var dataModel = {};

            // save attribute customizations
            dataModel.attributes = [];
            $.each(this.attribs, function(index, value) {
                dataModel.attributes.push(value.save());
            });

            // save filters
            dataModel.filters = this.caseFilter.save();

            // save selected cases
            if( this.selectedCases ){
                dataModel.selectedCases = Array.from(this.selectedCases);
            }
            if( this.selectedCategories ){
                dataModel.selectedCategories = Array.from(this.selectedCategories);
            }
            if( this.selectedNumericRange ){
                dataModel.selectedNumericRange = Dyn.dataUtil.clone(this.selectedNumericRange);
            }

            // save explicit order by attribute (0+  attribute index, or -1 for natural case order)
            // NOTE: it should be called 'orderByAttributeIndex', but backward compatibility would need to be maintained.
            if( this.getOrderByAttribute() ){
                dataModel.orderByAttributeId = this.getOrderByAttribute().id;
            }
            if( this.getSelectedAttribute() ){
                dataModel.selectedAttributeId = this.getSelectedAttribute().id;
            }
            // Get derived attributes
            if ( this.metaData && this.metaData.fields ){
                dataModel.derivedAttributes = this.metaData.fields.filter(function(field){return field.attribute_type === "derived"});
            }
            return dataModel;
        }

        restore( savedData, deferPlotUpdate ) {
            const deferEval = true;
            let whatChanged = new AttributeValuesNotifier();

            // restore MetaData for new and existing derived attributes
            if( savedData.derivedAttributes ) {
                let existingAttribMetaData = savedData.derivedAttributes.filter( attr =>
                    -1 !== this.metaData.fields.findIndex( field => field.id === attr.id ) );
                let newAttribMetaData = savedData.derivedAttributes.filter( attr =>
                        -1 === this.metaData.fields.findIndex( field => field.id === attr.id ) );
                // create new attributes
                if( newAttribMetaData.length ) {
                    this.addMetadataFields( newAttribMetaData, null, whatChanged, savedData.attributes, deferEval );
                }
                // update metadata for existing attributes (formula in particular)
                existingAttribMetaData.forEach( attrMetaData => {
                    this.updateMetadataField( {
                        id: attrMetaData.id,
                        name: attrMetaData.name,
                        longName: attrMetaData.long_name,
                        formulaString: attrMetaData.formula }, whatChanged, deferEval );
                } );
            }
            // restore attribute customizations (attributes may be new from MetaData)
            if( savedData.attributes ){
                // keeping it .ID, as previously saved plot state has .ID and not .id
                savedData.attributes.forEach( (value) => {
                    let existingAttribute = value.ID && this.getAttributeByID(value.ID);
                    if( existingAttribute ){
                        existingAttribute.restore(value);
                    }
                });
            }
            if( savedData.derivedAttributes && deferEval ) {
                // STEP2: after all attribute name updates, formulas that reference them can be evaluated
                let evaluatedIDs = this.evaluateFormulas(); // update case values dependent on the formulas
                whatChanged.updateNotifier( { attributeIDs: evaluatedIDs } );
                this.updateAllAttributeColors(); // update attribute count and the colors that depend on it
                this.checkData();
            }
            // NOTE: now that all attributes have been created, we can create filters and refer to selected and order-by attributes
            if( savedData.filters ){
                this.caseFilter.restore(savedData.filters, deferPlotUpdate);
            }

            // restore or clear case selections, with or without categories or numeric range
            this.selectedCases = ( savedData.selectedCases && savedData.selectedCases instanceof Array )
                ? savedData.selectedCases
                : [];
            this.selectedCategories = ( savedData.selectedCategories )
                ? savedData.selectedCategories
                : [];
            this.selectedNumericRange = ( savedData.selectedNumericRange )
                ? new Dyn.mathUtil.Range(savedData.selectedNumericRange.min, savedData.selectedNumericRange.max)
                : null;

            // if savedData.selectedAttributeId defined or not
            this.setSelectedAttribute({
                id: savedData.selectedAttributeId, // if undefined, clear selection
                noUndo: true // subroutines should not create their own undo step
            });
            // if savedData.orderByAttributeId defined or not
            if( Dyn.mathUtil.isNumber(savedData.orderByAttributeByID) ){
                // for backward compatibility check to see if 'orderByAttributeId' is a number
                // - old saved data had attribute Index (integer) instead of actual ID (string), OK to ignore
            } else {
                this.setOrderByAttribute({
                    id: savedData.orderByAttributeId, // if undefined, set to default case order
                    noUndo: true // subroutines should not create their own undo step
                });
            }
            // now notify other objects in case restore() is for an existing plot
            if( whatChanged.isAnythingChanged()){
                whatChanged.notify();
                mediator.publish( "card:reinstateCaseCard", {} );
                if( this.selectedCases ) {
                    const caseData = {
                        ids: this.selectedCases,
                        deferPlotUpdate: deferPlotUpdate };
                    mediator.publish( "records:preNotifyCasesSelected", caseData ); // do before final notification
                    mediator.publish( "records:setCasesSelected", caseData );
                }
            }
        }

        /**
         * Handle one or more caseTable cell changes,
         * by updating the corresponding attribute data and/or metadata.
         * In some cases attributes can be created or deleted; cases created or deleted.
         * @param changes - Array of [rowIndex, colIndex, oldValule, newValue]
         * @param firstTableRowIsAttributeName - if true, rowIndex 0 is for attribute name, else rowIndex 0 is first case.
         * @param [undoFlags] - optional undo/redo instructions
         */
        updateCaseTableValues( changes, firstTableRowIsAttributeName, undoFlags ) {
            console.log( "updateCaseTableValues", changes, firstTableRowIsAttributeName )
            let maxColIndexForTrim = -1,
                maxCasesToTrim = changes.length,
                //lastRowIndex = this.getNumCasesPreFilter() + (firstTableRowIsAttributeName?0:-1),
                whatChanged = new AttributeValuesNotifier(),
                undoData = {
                    deletedAttrs:[], oldNumCases:this.getNumCasesPreFilter(), oldNumAttrs:this.attribs.length };

            // update all case values; can also change the number of cases of all attributes to match
            changes.forEach(([rowIndex, colIndex, oldValue, newValue]) => {
                // console.log("updateCaseTableValues", "row", rowIndex, "col", colIndex, [ "oldValue", oldValue, "newValue", newValue,
                //     "normalized", Dyn.dataUtil.normalizeCaseValueString( newValue ), // convert "" to null, etc.,
                //     "originalCases", this._tableToAttributeRawValue( rowIndex, colIndex, firstTableRowIsAttributeName, false ),
                //     "cases", this._tableToAttributeRawValue( rowIndex, colIndex, firstTableRowIsAttributeName, true ) ]);
                maxColIndexForTrim = Math.max( maxColIndexForTrim, colIndex );
                this._updateCaseTableValue( rowIndex, colIndex, newValue, firstTableRowIsAttributeName, whatChanged, maxCasesToTrim );
            });
            // Evaluate dependent formulas, if any
            let toEvalAttribs = this.findAttributesToEvaluate( whatChanged.attributeIDs );
            if( toEvalAttribs.length ) {
                let evaluatedIDs = this.evaluateFormulas( toEvalAttribs );
                whatChanged.updateNotifier( {
                    attributeIDs: evaluatedIDs, caseValueUpdate: true
                });
            }
            // delete any attributes that can be trimmed from end of data set
            if( maxColIndexForTrim >= 0 ) { // if there was an attempted change in any column
                let changesThatDeleteAttributeName = changes.filter(([rowIndex, colIndex, oldValue, newValue]) =>
                        ((rowIndex === 0) && oldValue && ! newValue)),
                    idsOfDeletedAttributeNames = changesThatDeleteAttributeName.map( c => this.columnIds[c[1]] ),
                    attributesToDelete = this.calcTrimmedAttributes( maxColIndexForTrim, idsOfDeletedAttributeNames );
                attributesToDelete.forEach(id => {
                    //console.log("updateCaseTableValues delete attribute ", id);
                    undoData.deletedAttrs.push( this.deleteMetadataField({id: id}, whatChanged));
                });
            }
            if( whatChanged.isAnythingChanged()) {
                // save undo step
                if( ! (undoFlags && undoFlags.noUndo)) {
                    undoData.cellChanges = Dyn.dataUtil.clone( changes );
                    undoData.firstTableRowIsAttributeName = firstTableRowIsAttributeName;
                    undoData.actionID = "updateCaseTableValues";
                    undoData.actionString = Dyn.loc("Edit Table");
                    Dyn.undoManager.saveUndoStep( undoData, this, (undoFlags && undoFlags.isRedo));
                }
                // notify plot, case card, case table
                whatChanged.notify();
            } else {
                // refresh table to undo insignificant changes (e.g. 1 => 1.00, revert to 1, "Mar" => "March", revert to "Mar)
                console.log("updateCaseTableValues is ignoring edit, changed is insignificant (e.g. trailing zeros)")
                whatChanged.updateNotifier({ isIgnoredEdit: true });
                whatChanged.notify();
            }
        }

        /**
         * Undo actions of updateCaseTableValues(), which is used for
         * entering, pasting, cutting or deleting cell vales.
         * @param undoData - data for undo step saved by updateCaseTableVales()
         */
        undoCaseTableValues( undoData ) {
            let newOrderBy, newSelected, newCaseFilter;

            // undo step 1: restore any deleted attribute settings if Do deleted them
            if( undoData.deletedAttrs ){
                let newFields = undoData.deletedAttrs.map( a => a.metaData[0] ),
                    newAttributeCases = undoData.deletedAttrs.map( a => a.attribute[0].originalCases ),
                    newNotifier = new AttributeValuesNotifier();
                // create the attributes and notify other objects
                if( newFields.length ) {
                    this.addMetadataFields( newFields, newAttributeCases, newNotifier );
                }
            }
            // undo step 2: re-apply the old cell values
            //  Note: possibly create new cases to hold them if Do was a delete of cells
            if( undoData.cellChanges ){
                let swappedCellChanges = undoData.cellChanges.map(
                    ([rowIndex, colIndex, oldValue, newValue]) =>
                     [rowIndex, colIndex, newValue, oldValue]
                );
                this.updateCaseTableValues(
                    swappedCellChanges,
                    undoData.firstTableRowIsAttributeName,
                    { noUndo:true });
            }
            // undo step 3: remove attributes created by Do
            while( this.attribs.length > undoData.oldNumAttrs ) {
                let lastAttrID = this.attribs[this.attribs.length-1].id;
                this.deleteMetadataField({ id:lastAttrID });
            }
            // undo step 4: undo number of cases change from cut or paste
            this.setNumCases( undoData.oldNumCases );
            // undo final steps: restore plot settings?
            undoData.deletedAttrs && undoData.deletedAttrs.forEach( a => {
                if( Number.isFinite( a.orderByAttribute)){
                    this.setOrderByAttribute({id: a.attribute[0].id, object: "undo-redo", noUndo: true});
                }
                if( Number.isFinite( a.selectedAttribute)) {
                    this.setSelectedAttribute({id: a.attribute[0].id, object: "undo-redo", noUndo: true});
                }
                if( a.attributeFilter ) {
                    this.caseFilter.filterByAttribute(a.attributeFilter );
                }
            });

            Dyn.assert( this.attribs.length === undoData.oldNumAttrs );
            Dyn.assert( this.getNumCasesPreFilter() === undoData.oldNumCases );
        }

        /** update the dataset when a case table value changes.
         * @param rowIndex -- row 0 === attribute name, row 1+ == case 1+
         * @param colIndex -- one attribute per column
         * @param newValue
         * @param firstTableRowIsAttributeName {boolean}
         * @param whatChanged { AttributeValuesNotifier } -- notifier object to update
         * @param maxCasesToTrim
         * @returns {boolean}
         */
        _updateCaseTableValue( rowIndex, colIndex, newValue, firstTableRowIsAttributeName, whatChanged, maxCasesToTrim ) {
            let changed = false,
                attribIndex = colIndex,
                caseIndex = ( firstTableRowIsAttributeName ? rowIndex - 1 : rowIndex ),
                isExistingAttribute = ( Dyn.mathUtil.isInteger(attribIndex )
                    && (attribIndex >= 0)
                    && (attribIndex < this.getNumAttributes()));
            if( isExistingAttribute ) {
                let attribID = this.attribs[attribIndex].id;
                if( caseIndex === -1 ) {
                    // update attribute name if possible, change to default if new value is blank
                    let newAttrName = newValue || Dyn.dataUtil.defaultAttributeName(colIndex);
                    if( Dyn.dataUtil.isValidAttributeName( newAttrName )
                        && (newAttrName !== this.attribs[attribIndex].name )) {
                        // note: name and long_name are the same unless editing individual attribute properties
                        changed = this.updateMetadataField({
                            id:attribID, name:newAttrName, longName:newAttrName }, whatChanged);
                    }
                } else {
                    // update case value if possible
                    changed = this._updateCaseAttributeValue( attribIndex, caseIndex, newValue, whatChanged, maxCasesToTrim );
                }
            } else {
                // add the necessary blank attributes and then set a single attribute name or value
                // note that multiple attributes can be created;
                changed = this._addAttributeAt( attribIndex, caseIndex, newValue, whatChanged );
            }
            return changed;
        }

        /**
         * Get the Attribute name or Attribute case value to match the given table cell index.
         * Internal use only; see getTableRowData(), getTableColumnData()
         * @param rowIndex
         * @param colIndex
         * @param firstTableRowIsAttributeName {boolean}
         * @param original {boolean}
         * @returns {null|string|number}
         */
        _tableToAttributeRawValue( rowIndex, colIndex, firstTableRowIsAttributeName, original ) {
            let caseIndex = ( firstTableRowIsAttributeName ? rowIndex - 1 : rowIndex ),
                attr = this.attribs[ colIndex ],
                val = Dyn.dataUtil.kMissingValue;
            if( attr ) {
                if( caseIndex === -1 ) { // row 0 or row -1
                    val = attr.name;
                } else if( caseIndex >= 0 && caseIndex < attr.originalCases.length ){
                    val = original ? attr.cases[caseIndex] : attr.originalCases[caseIndex];
                }
            }
            return val;
        }

        /**
         * Update the case value of an existing attribute, possibly adding or removing cases from that attribute,
         * @param attribIndex
         * @param caseIndex
         * @param newValue
         * @param whatChanged { AttributeValuesNotifier } -- notifier object to update
         * @param maxCasesToTrim { number } -- limit on how many cases can be trimmed
         * @returns {boolean} -- true if changed
         */
        _updateCaseAttributeValue( attribIndex, caseIndex, newValue, whatChanged, maxCasesToTrim ) {
            let attrib = this.attribs[attribIndex],
                oldNumCases = this.getNumCasesPreFilter(),
                newNumCases,
                changed = false;
            if( attrib && caseIndex >= 0 ) {
                changed = attrib.setCaseValues( caseIndex, [ newValue ]);
                if( (caseIndex+1) === oldNumCases ) {
                    newNumCases = this.calcTrimmedNumberOfCases( maxCasesToTrim );
                    changed = changed || ( oldNumCases!==newNumCases ); // trim on update even if no change
                } else {
                    newNumCases = Math.max( oldNumCases, attrib.originalCases.length ); // increase number of cases?
                }
            }
            if( changed ) {
                // update other attributes in the dataset
                let isCaseLengthUpdate = this.setNumCases( newNumCases ); // existing attribute case length change?
                // update any other attributes that reference this attribute by formula
                let idsOfUpdatedAttributes = [attrib.id],
                    evaluatedIDs = this.evaluateFormulas( [attrib] );
                this.checkData();

                // update notifier so caller knows how to refresh plot, cards, table, etc
                whatChanged.updateNotifier( {
                    attributeIDs: idsOfUpdatedAttributes.concat(evaluatedIDs),
                    numCasesUpdate: isCaseLengthUpdate,
                    caseValueUpdate: true
                });
            }
            Dyn.assert( changed === whatChanged.isAnythingChanged());
            return changed;
        }

        /**
         * Add 1+ attributes to hold the new user-supplied value, with fill.
         * e.g. if 2 attributes and adding the 5th,
         * the 3rd and 4th and 5th attributes will be created, with
         * 3rd and 4th having all missing values.
         * Useful for adding cells to the table in a spreadsheet-like way.
         * @param attribIndex -- index of new attribute
         * @param caseIndex -- case index (-1 for attribute name)
         * @param newValue -- case value or attribute name
         * @param whatChanged -- { AttributeValuesNotifier } notifier object to update
         */
        _addAttributeAt( attribIndex, caseIndex, newValue, whatChanged ) {
            let newAttributeCases = [],
                newFields = [];
            // for each new attribute, fill in the attribute data
            for( let i=this.getNumAttributes(), j=attribIndex; i<=j; i++ ) {
                let newNumCases = Math.max(this.getNumCasesPreFilter(), caseIndex + 1),
                    newCases = Dyn.dataUtil.filledArray(newNumCases, Dyn.dataUtil.kMissingValue),
                    attrName = Dyn.dataUtil.defaultAttributeName( i );
                if( i === attribIndex ){
                    if( (caseIndex < 0) && Dyn.dataUtil.isValidAttributeName(newValue) ) {
                        // new value is the attribute name
                        attrName = newValue; // new value is the attribute name
                    } else if( caseIndex >= 0 ){
                        // new value is a case value
                        newCases[caseIndex] = Dyn.dataUtil.normalizeCaseValueString(newValue);
                    }
                }
                let attribMetaData = MetaData.newMetaDataFieldFromName( attribIndex, attrName );
                newFields.push( attribMetaData );
                newAttributeCases.push( newCases );
            }
            // create the attributes and notify other objects
            if( newFields.length ) {
                this.addMetadataFields( newFields, newAttributeCases, whatChanged );
            }
        }


        /**
         * Add a new attribute to the dataset, defined by the formula
         * @param args.name
         * @param args.longName
         * @param args.formulaString
         */
        addMetadataField( args ) {
            let attribIndex = this.metaData.fields.length,
                attribMetaData = MetaData.newMetaDataFieldFromName( attribIndex, args.name, args.longName, args.formulaString ),
                whatChanged = new AttributeValuesNotifier();

            this.addMetadataFields([attribMetaData], null, whatChanged );
            whatChanged.notify();
        }

        /**
         * Add new attributes to the dataset,
         * with case values defined either by a formula or by the given case values
         * @param metaDataArray -- new metaData values
         * @param optionalCases -- null | array of new case values
         * @param whatChanged { AttributeValuesNotifier } -- notifier to update with changes made
         */
        addMetadataFields( metaDataArray, optionalCases, whatChanged, deferEval=false ) {
            const maxCallback = (m, array) => Math.max(m, array.length);
            let oldNumCases = this.getNumCasesPreFilter(),
                optNumCases = (optionalCases ? optionalCases.reduce(maxCallback, 0) : 0),
                newNumCases = Math.max(oldNumCases, optNumCases),
                blankCases = Dyn.dataUtil.filledArray(newNumCases, Dyn.dataUtil.kMissingValue),
                idsOfUpdatedAttributes = [],
                isFormulaUpdate = false;

            // add the new attributes and associated this.metaData.fields, this.columnIds, this.columnNames.
            metaDataArray.forEach((attribMetaData, i) => {
                let attribIndex = this.metaData.fields.length,
                    attribCases = optionalCases ? optionalCases[i] : blankCases;

                this.metaData.fields.push(attribMetaData);
                this.columnIds.push(attribMetaData.id);
                this.columnNames.push(attribMetaData.name);

                this.attribs.push(
                    MetaData.newAttributeFromMetaData(
                        this.columnIds[attribIndex],
                        this.columnNames[attribIndex],
                        attribCases,
                        this.metaData.fields[attribIndex]
                    )
                );
                if( attribMetaData.formula ){
                    isFormulaUpdate = true;
                }
                idsOfUpdatedAttributes.push(attribMetaData.id);
                let debugAttribute = this.attribs[this.attribs.length-1];
                console.log("DataSet.addMetadataFields", "added attribute", debugAttribute.name, debugAttribute.getDataType( true ));
            }, this);

            // update other attributes in the dataset
            let isCaseLengthUpdate = this.setNumCases(newNumCases); // existing attribute case length change?
            if( ! deferEval ) {
                let evaluatedIDs = this.evaluateFormulas(); // update case values dependent on the formulas
                idsOfUpdatedAttributes = idsOfUpdatedAttributes.concat(evaluatedIDs);
                this.updateAllAttributeColors(); // update attribute count and the colors that depend on it
                this.checkData();
            }
            if( isCaseLengthUpdate ){
                idsOfUpdatedAttributes = []; // all attributes changed
            }

            // update notifier so caller can refresh plot, cards, table, etc
            whatChanged.updateNotifier({
                    attributeIDs: idsOfUpdatedAttributes,
                    formulaUpdate: isFormulaUpdate,
                    addAttribute: true,
                    numCasesUpdate: isCaseLengthUpdate
                });
        }

        /**
         * Update the metadata for an attribute in this dataset.
         * updates: this.metadata, this.attributes, this.columnNames
         * track changes to this dataset, so we don't refresh plot, table, card unnecessarily
         * @param args {{ id:string, name:string, longName:string, formulaString:string }}
         * @param [whatChanged] { AttributeValuesNotifier } -- optional notifier, if non-null then caller notifies
         * @returns {boolean} -- true if an attribute was changed
         */
        updateMetadataField( args, whatChanged, deferEval=false) {
            Dyn.assert(typeof args.ID === 'undefined', "Uppercase 'ID' not allowed, use lower case 'id' instead");
            Dyn.assert(typeof args.formula === 'undefined', "Use 'formulaString' not 'formula'");
            let changed = false,
                immediateNotification = !(whatChanged);
            whatChanged = whatChanged || new AttributeValuesNotifier();

            if( !args.id ){
                return;
            }
            // Updating the metaData field
            let fieldIndex = this.metaData.fields.findIndex( f => f.id === args.id ),
                field = this.metaData.fields[fieldIndex],
                isNewString = ( newStr, oldStr ) => ((typeof newStr==="string") && newStr !== oldStr );
            if( field ){
                if( isNewString( args.name, this.columnNames[fieldIndex])) {
                    this.columnNames[fieldIndex] = args.name;
                    changed = true;
                }
                if( isNewString( args.name, field.name)) {
                    whatChanged.updateNotifier({ nameUpdate:true });
                    field.name = args.name;
                    changed = true;
                }
                if( isNewString( args.longName,  field.long_name )) { // note difference in field names
                    whatChanged.updateNotifier({ descriptionUpdate:true });
                    field.long_name = args.longName;
                    changed = true;
                }
                if( isNewString( args.formulaString, field.formula )) { // note difference in field names
                    field.formula = args.formulaString;
                    changed = true;
                }
            }
            // Updating the attribute object to refresh the plot view values
            let attributeObj = Dyn.dataSet.getAttributeByID(args.id);
            if( attributeObj ){
                if( isNewString( args.name, attributeObj.name )
                    || isNewString( args.longName, attributeObj.longName )) {
                    attributeObj.setName(args.name);
                    attributeObj.setLongName(args.longName || '');
                    changed = true;
                }
                if( isNewString( args.formulaString, attributeObj.formulaString )) {
                    whatChanged.updateNotifier( { formulaUpdate: true } );
                    if( attributeObj.setFormula( args.formulaString || '', false, true ) ) {
                        if( ! deferEval ) {
                            let evaluatedIDs = this.evaluateFormulas( [ attributeObj ] ); // evaluate this formula and other formulas that depend on it
                            whatChanged.updateNotifier( { attributeIDs: evaluatedIDs } );
                        }
                    }
                    changed = true;
                }
            }

            // Refresh plot, cards, table, etc ONLY if we created our own notification
            if( changed ) {
                whatChanged.updateNotifier({ attributeIDs:[args.id] });
            }
            if( immediateNotification && whatChanged.isAnythingChanged() ) {
                whatChanged.notify();
            }   // else caller will notify
            return changed;
        }

        /**
         * Delete and remove a formula attribute from this dataset, that was created with addMetadataField()
         * @param args { id:{string} } - id of Dyn.Attribute in this dataset
         * @param [whatChanged] { AttributeValuesNotifier } -- optional notifier, if non-null then caller notifies
         */
        deleteMetadataField( args, whatChanged ) {
            if( !args.id ){
                return;
            }
            var attributeObj = Dyn.dataSet.getAttributeByID(args.id),
                attributeIndex = this.columnIds.indexOf(attributeObj.id),
                immediateNotification = !(whatChanged),
                undoData = { attributeIndex:attributeIndex };
            whatChanged = whatChanged || new AttributeValuesNotifier();
            whatChanged.updateNotifier({
                attributeIDs:[args.id] ,
                formulaUpdate: true,
                deleteAttribute: true
            });

            // remove the attribute from data set
            undoData.metaData = this.metaData.fields.splice(attributeIndex, 1);
            this.columnIds.splice(attributeIndex, 1);
            this.columnNames.splice(attributeIndex, 1);
            undoData.attribute = this.attribs.splice(attributeIndex, 1);

            // update any references to these attributes
            if( this.orderByAttribute === attributeIndex ) {
                undoData.orderByAttribute = attributeIndex;
                this.orderByAttribute = -1;
            }
            if( this.selectedAttribute === attributeIndex ) {
                undoData.selectedAttribute = attributeIndex
                this.selectedAttribute = -1;
            }
            if( this.caseFilter.getAttributeFilter( args.id )) { // delete associated attribute filter?
                undoData.attributeFilter = this.caseFilter.getAttributeFilter( args.id );
                this.caseFilter.filterByAttribute({ attributeID:args.id }, true/* deferPlotUpdate */);
            }
            let evaluatedIDs = this.evaluateFormulas(); // update any formulas that might have depended on the deleted attribute
            whatChanged.updateNotifier({ attributeIDs:evaluatedIDs });
            this.updateAllAttributeColors(); // update attribute count and the colors that depend on it

            // Refresh plot, cards, table, etc
            if( immediateNotification && whatChanged.isAnythingChanged() ) {
                whatChanged.notify();
            }   // else caller will notify
            return undoData;
        }

        /**
         * Update the metaData after a change to attributes
         */
        updateMetaDataNamesFromAttributes() {
            let changed = false;
            if( this.metaData && this.metaData.fields ) {
                this.attribs.forEach((attribute, i) => {
                    Dyn.assert(this.columnIds[i] === attribute.id);
                    let f = this.metaData.fields[i];
                    if( f &&
                        (this.columnNames[i] !== attribute.name ||
                        f.name !== attribute.name ||
                        f.long_name !== attribute.longName )) { // field.long_name maps to attribute.longName
                            // console.log("updateMetaDataNamesFromAttributes", f.name, f.long_name, this.columnNames[i], attribute.name, attribute.longName);
                            this.columnNames[i] = attribute.name;
                            f.name = attribute.name;
                            f.long_name = attribute.longName;
                            changed = true;
                    }
                });
            }
            return changed;
        }

        undo(undoableData) {
            switch(undoableData.actionID){
                case "selectAttributeByID":
                    // (re-) select the given attribute, without creating any new undo step (caller creates redo step)
                    this.setSelectedAttribute({id: undoableData.undoAttribID, object: "undo-redo", noUndo: true});
                    break;
                case "orderByAttributeByID":
                    // (re-) order-by the given attribute, without creating any new undo step (caller creates redo step)
                    this.setOrderByAttribute({id: undoableData.undoAttribID, object: "undo-redo", noUndo: true});
                    break;
                case "updateCaseTableValues":
                    this.undoCaseTableValues( undoableData );
                    break;
                case "moveCategoryOfAttribute":
                    this.moveCategoryOfAttribute( {
                        attributeID: undoableData.attributeID,
                        oldIndex: undoableData.newIndex, newIndex: undoableData.oldIndex, // swap indices to undo
                        noUndo: true } );
                    break;
                default:
                    console.log("Unknown undo action: " + undoableData.actionID);
            }
        }

        redo(undoableData) {
            switch(undoableData.actionID){
                case "selectAttributeByID":
                    // select the previously-selected attribute, and create an undo-of-redo step
                    this.setSelectedAttribute({
                        id: undoableData.redoAttribID,
                        object: "undo-redo",
                        noUndo: false,
                        isRedo: true
                    });
                    break;
                case "orderByAttributeByID":
                    // order by the previous-order-by attribute, without creating any new undo step (caller creates redo step)
                    this.setOrderByAttribute({
                        id: undoableData.redoAttribID,
                        object: "undo-redo",
                        noUndo: false,
                        isRedo: true
                    });
                    break;
                case "updateCaseTableValues":
                    // just re-apply cell changes to redo
                    this.updateCaseTableValues(
                        undoableData.cellChanges,
                        undoableData.firstTableRowIsAttributeName,
                        { noUndo:false, isRedo:true });
                    break;
                case "moveCategoryOfAttribute":
                    this.moveCategoryOfAttribute( {
                        attributeID: undoableData.attributeID,
                        oldIndex: undoableData.oldIndex, newIndex: undoableData.newIndex,
                        noUndo: false, isRedo:true } );
                    break;
                default:
                    console.log("Unknown redo action: " + undoableData.actionID);
            }
        }

        /**
         * Get the number of cases in the data set, before filtering, post Sampling.
         * Defined as the number of cases in the longest attribute.
         *
         * See also getNumCasesAfterFilter()
         * Note: number of cases does not match this.tableRows after sampling, see this.getPopulationSize()
         * @returns {number} -- number of cases, 0+
         */
        getNumCasesPreFilter() {
            // var numCases = 0;
            // this.attribs.forEach( function( attribute ) {
            //     var attribNumCases = attribute.originalCases.length;
            //     if( numCases < attribNumCases ) {
            //         numCases = attribNumCases;
            //     }
            // });
            // assume that we have a 1st attribute of case indices that matches the other attribute lengths
            //Dyn.assert( this.attribs[0].originalCases.length === this.tableRows.length );
            return this.attribs.length ? this.attribs[0].originalCases.length : 0;
        }

        /**
         * Get number of cases in plot post filter, post sampling.
         * See also getNumCasesPreFilter(), getPopulationSize()
         * @return {number}
         */
        getNumCasesAfterFilter() {
            return this.getNumCasesPreFilter() - this.caseFilter.numCasesFiltered;
        }

        getNumAttributes() {
            Dyn.assert(this.attribs.length === this.columnIds.length);
            return this.attribs.length;
        }

        /*
         * number of cases after the filter is applied
         * but before the sample is taken (if any sample taken)
         */
        getPopulationSize() {
            return (this.caseSampler.hasSamples() ?
                this.caseSampler.pop.numCasesAfterFilter :
                this.getNumCasesAfterFilter());
        }

        /*
         * array of case index after the filter is applied
         * before any sample taken ( if any active )
         */
        getPopulationCaseArray() {
            return (this.caseSampler.hasSamples() ?
                this.caseSampler.pop.caseIndices :
                this.caseFilter.getCasesAfterFilter());
        }

        /**
         * Get the case data for a column of the case table.
         * @param columnIndex
         * @returns {Array|*}
         */
        getTableColumnData(columnIndex) {
            let numColumns = this.getNumAttributes(),
                columnData = [];
            if( columnIndex >= 0 && columnIndex < numColumns ){
                // return a copy of the attribute case data for the nth attribute
                columnData = this.attribs[columnIndex].cases.map(a => a);
            }
            return columnData;
        }

        /**
         * Get the case data for row of the case table.
         * @param rowIndex
         * @param options {{rawData,formatted}}
         * @returns {*[]}
         */
        getTableRowData( rowIndex, options ) {
            let numRows = this.getNumCasesPreFilter(),
                rowData = [];
            if( rowIndex >= 0 && rowIndex < numRows ){
                // return a copy of the attribute case data for the nth case
                for( let i = 0, j = this.getNumAttributes(); i < j; i++ ){
                    rowData.push(this.attribs[i].caseValueFormatted(rowIndex, options));
                }
            }
            return rowData;
        }

        /**
         * Get the selected attribute object,
         * used to color the cases (and shown in the legend)
         * (or null if order by natural case order)
         * @returns {Dyn.Attribute | null}
         */
        getSelectedAttribute() {
            return (this.selectedAttribute >= 0 ?
                this.attribs[this.selectedAttribute] : null);
        }

        // get an attribute object (type Dyn.Attribute) by column name
        getAttributeByName(columnName) {
            var attribIndex = Dyn.dataSet.columnNames.indexOf(columnName);
            return (attribIndex >= 0 ? this.attribs[attribIndex] : null);
        }

        // get an attribute object (type Dyn.Attribute) by column ID
        getAttributeByID(columnID) {
            var attribIndex = Dyn.dataSet.columnIds.indexOf(columnID);
            return (attribIndex >= 0 ? this.attribs[attribIndex] : null);
        }

        getAttributeByIndex(attribIndex) {
            return (attribIndex >= 0 ? this.attribs[attribIndex] : null);
        }

        /**
         * [isAnyAttributeHidden check if at least one attrib is marked as hidden]
         * @return {Boolean} [if any attribute is hidden.]
         */
        isAnyAttributeHidden() {
            return this.attribs.some((attr) => (attr.isVisible === false));
        }

        // return true if there are 1+ categories selected, but it is not the given category
        isDeselectedCategory(categoryValue) {
            return (
                this.selectedCategories.length &&
                this.selectedCategories.indexOf(categoryValue) === -1);
        }

        // for clients to check if category is selected along with cases, after "records:setCasesSelected" message
        isCategorySelected(categoryValue) {
            return (this.selectedCategories.indexOf(categoryValue) >= 0);
        }

        // for clients to check if case is selected, after "records:setCasesSelected" message
        isCaseSelected(caseIndex) {
            return (this.selectedCases.indexOf(caseIndex) >= 0);
        }

        // return true if there are 1+ case selected, but it is not the given case
        isDeselectedCase(caseIndex) {
            return (
                this.selectedCases.length &&
                this.selectedCases.indexOf(caseIndex) === -1);
        }

        /** if cases are selected by numeric range, get the range, or null */
        getSelectedByNumericRange() {
            if( this.selectedNumericRange && this.selectedNumericRange.isFinite() ){
                return this.selectedNumericRange;
            }
            return null;
        }

        isCaseFilteredOut(caseIndex) {
            // note: it is OK if caseIsFiltered.length < caseIndex
            // e.g. no filtering if caseIsFiltered == [];
            return (this.caseFilter.caseIsFiltered[caseIndex]);
        }

        isCaseMissing(caseIndex) {
            // note: it is OK if caseIsMissing.length < caseIndex
            // e.g. no missing cases if caseIsMissing == [];
            return (this.missingCases.caseIsMissing[caseIndex]);
        }

        /**
         * Test for cases that are plotted, instead of being moved to the excluded cases area.
         * Valid after PlotElement.calcMissingValueCases() phase of plot updates.
         */
        isCaseNonMissingNonFiltered(caseIndex) {
            return !(this.isCaseMissing(caseIndex) || this.isCaseFilteredOut(caseIndex));
        }

        /**
         * Handle a notification that an attribute in the dataset has a new data type.
         * Then notify dependencies (plots, tables, etc) so they can update.
         * @param attributeData { attributeID, dataType } - values that match Dyn.Attribute fields.
         */
        updateAttributeDataType(attributeData) {
            // get the attribute details
            var attribute = Dyn.dataSet.getAttributeByID(attributeData.attributeID),
                splitType, type, subtype;

            // check if the passed datatype is not same as current attribute datatype
            if( attribute && attribute.getDataType(true) !== attributeData.dataType ){
                // split "type:subtype" into two parameters if applicable
                splitType = attributeData.dataType.split(":");
                type = splitType[0] || attributeData.dataType;
                subtype = splitType[1] || undefined;
                attribute.setDataType(type, subtype, true /* update cases and stats now */);
                // update views to show new data type
                (new AttributeMetadataNotifier({
                    attributeIDs:[ attribute.id ],
                    dataTypeUpdate: true
                })).notify();
            }
        }

        /**
         * Respond to a published message "records:attributeNumericDecimalsUpdated"
         * @param attributeData.attributeID -- ID of attribute that was changed
         * @param attributeData.numericDecimals -- integer value for numeric decimals metadata
         */
        updateAttributeNumericDecimals(attributeData) {
            // get the attribute details
            var attribute = Dyn.dataSet.getAttributeByID(attributeData.attributeID);
            // check if the passed datatype is not same as current attribute datatype
            if( attribute && attribute.getDataType() === Dyn.dataUtil.types.kNumerical ){
                attribute.setNumericDecimals(attributeData.numericDecimals);
                (new AttributeMetadataNotifier({
                    attributeIDs:[ attribute.id ],
                    numericDecimalsUpdate:true
                })).notify();
            }
        }

        /**
         * Respond to a published message "records:attributeNumericIntervalsUpdated"
         * @param attributeData.attributeID -- ID of attribute that was changed
         * @param attributeData.numericIntervalWidth -- undefined or positive numeric interval width
         * @param attributeData.numericIntervalStart -- undefined or numeric value (default is 0)
         */
        updateAttributeNumericIntervals(attributeData) {
            // get the attribute details
            var attribute = Dyn.dataSet.getAttributeByID(attributeData.attributeID);
            // set the parameters
            if( attribute && attribute.setNumericIntervalParameters(attributeData.numericIntervalWidth, attributeData.numericIntervalStart) ){
                // Reset attribute specific filter
                this.caseFilter.resetFilterByAttributeId(attribute.id);
                // if the values have changed the dataset, tell views to refresh/recalculate plot
                (new AttributeMetadataNotifier({
                    attributeIDs:[ attribute.id ],
                    numericIntervalsUpdate:true
                })).notify();
            }
        }

        /*
         * set the visibility of the attributes been passed to false, else mark
         * it as true.
         */
        updateAttributesDisplay(attributeList) {

            // iterate thru this.attribs and if its in attributeList, mark
            // isVisible - false, second parameter is fromCaseCard
            var fromCaseCard = true;
            var metadata = this.metaData;
            this.attribs.forEach(function(attr) {
                // if (attributeList.attributes.includes(attr.id)) {
                var attrMeta = metadata.fields.find(meta => meta.id === attr.id);
                if( attributeList.attributes.indexOf(attr.id) >= 0 ){
                    attr.setVisibility(false, fromCaseCard);
                    if (attrMeta) {
                        // Setting metadata visibility flag
                        attrMeta.visibility = false;
                    }
                } else {
                    attr.setVisibility(true, fromCaseCard);
                    if (attrMeta) {
                        // Removing metadata visibility flag
                        delete attrMeta.visibility;
                    }
                }
            });

            // TableView - to hide the respective columns
            // currently this message used while adding derived attribute and
            // on showing/hiding attribute value
            mediator.publish("records:setAttributeValues", this);
        }

        /**
         * Handle a notification that an attribute in the dataset has a new sort order.
         * Then notify dependencies (plots, tables, etc) so they can update.
         * @param attributeData.attributeID - attribute to be updated
         * @param attributeData.sortOrder
         */
        updateAttributeSortOrder(attributeData) {

            // get the attribute details
            var attribute = Dyn.dataSet.getAttributeByID(attributeData.attributeID);

            // add logic to check current sort order
            attribute.setSortOrder(attributeData.sortOrder, true /* immediate update of stats */);

            // notify views to refresh
            (new AttributeMetadataNotifier({
                attributeIDs:[ attribute.id ],
                sortOrderUpdate:true
            })).notify();
        }

        /**
         * Handle a request to move the position of a category in an attribute's list of categories.
         * @param data {{attributeID, oldIndex, newIndex}}
         */
        moveCategoryOfAttribute( data ) {
            // get the attribute, update the position of a single category
            let attribute = Dyn.dataSet.getAttributeByID( data.attributeID);
            Dyn.assert( attribute && data.attributeID && data.oldIndex >= 0 && data.newIndex >= 0 );
            let success = attribute.moveOneCategory( data.oldIndex, data.newIndex );
            Dyn.assert( success, "error, unable to move category of attribute" );

            if( success ) {
                if( ! data.noUndo ) {
                    Dyn.undoManager.saveUndoStep( {
                        attributeID: data.attributeID,
                        oldIndex: data.oldIndex,
                        newIndex: data.newIndex,
                        actionID: "moveCategoryOfAttribute",
                        actionString: Dyn.loc( "Move Category of {{attribute}}", {
                            attribute: attribute.name
                        } )
                    }, this, data.isRedo );
                }

                // update plot and panels to show new category order
                (new AttributeMetadataNotifier({
                    attributeIDs:[ data.attributeID ],
                    categoryOrderUpdate:true
                })).notify();
            }
        }

        /**
         * Handle a notification that the color scheme has changed
         * affecting attribute colors.
         * @param data
         */
        updateAllAttributeColors(data) {
            let hasUncoloredCaseAttribute = this.attribs.length && this.attribs[0].isCaseAttribute,
                numAttributes = this.attribs.length,
                offset = hasUncoloredCaseAttribute ? 1 : 0;
            this.attribs.forEach((attribute, index) => {
                // assign colors, ignoring the "Case" attribute which has no color (#1230)
                if( ! attribute.isCaseAttribute ) {
                    attribute.setAttributeColorScheme(index-offset, numAttributes-offset);
                }
            });
        }

        /**
         * select the given attribute in the dataset,
         * -- selected attribute determines the case color
         * -- selected attribute also sets the order by attribute
         * and publish the message to select on other components
         * @param attributeData.id - attribute to be updated, or undefined to clear the selection
         */
        setSelectedAttribute(attributeData) {
            //Dyn.time( "setSelectedAttribute", "Start");
            var newAttribID = attributeData.id,
                newAttribIndex = this.columnIds.indexOf(newAttribID);

            if( newAttribIndex < 1 ){
                // visible attribute index is 1+, or -1 for clearing legend/color/selection
                newAttribIndex = -1;
            }

            // do nothing if multiple clicks of selecting the same attribute
            let isNewAttributeSelected = (
                this.selectedAttribute !== newAttribIndex ||
                (this.selectedAttributeAlsoOrders && this.orderByAttribute !== newAttribIndex));

            // save undo step before change
            let addUndoStep = ((!attributeData.noUndo) && (attributeData.isRedo || isNewAttributeSelected));
            if( addUndoStep ){
                let attribNameForUndo = ((newAttribIndex >= 0) ? this.attribs[newAttribIndex].name : ""),
                    oldAttribID = this.getSelectedAttribute() ? this.getSelectedAttribute().id : undefined;
                Dyn.undoManager.saveUndoStep({
                    redoAttribID: newAttribID,
                    undoAttribID: oldAttribID,
                    actionID: "selectAttributeByID",
                    actionString: Dyn.loc(attribNameForUndo ? "Select {{name}}" : "Deselect Attribute", {"name": attribNameForUndo})
                }, this, attributeData.isRedo);
            }

            // make the change
            if( isNewAttributeSelected ){
                // clear numeric intervals of old attribute, if any, so they are not re-used the next time attribute is selected
                let oldAttribute = this.getSelectedAttribute();
                if( oldAttribute ){
                    oldAttribute.setNumericIntervals(false);
                }
                // save state
                this.selectedAttribute = newAttribIndex;
                if( this.selectedAttributeAlsoOrders ){
                    this.orderByAttribute = newAttribIndex;
                }
                // notify clients
                attributeData.attrib = this.getSelectedAttribute();
                if( this.selectedAttributeAlsoOrders ){
                    attributeData.deferPlotUpdate = true; // only update plot with 2nd notification
                    mediator.publish("records:setSelectedAttribute", attributeData);
                    attributeData.deferPlotUpdate = false;
                    mediator.publish("records:setOrderByAttribute", attributeData);
                } else {
                    mediator.publish("records:setSelectedAttribute", attributeData);
                }
            }
            //Dyn.time( "setSelectedAttribute", "End");
        }

        /**
         * Get the attribute used to order cases in the plot
         * (or null if order by natural case order)
         * @returns {Dyn.Attribute | null}
         */
        getOrderByAttribute() {
            return (this.orderByAttribute >= 0 ?
                this.attribs[this.orderByAttribute] : null);
        }

        /**
         * Set or clear the "order by" attribute
         * and publish the message to select on other components.
         * Used to order cases in plots.
         * @param attributeData.id {string} -- attribute ID string or undefined to set to the default (case order)
         * @param attributeData.noUndo {boolean} -- if true, do not create a new undo step
         */
        setOrderByAttribute(attributeData) {
            //Dyn.time( 'setOrderByAttribute', 'Start' );
            var newAttribID = attributeData.id,
                newAttribIndex = this.columnIds.indexOf(newAttribID);

            if( newAttribIndex < 1 ){
                /// visible attribute index is 1+, or -1 for order by case (default ordering)
                newAttribIndex = -1;
            }

            // do nothing if multiple clicks of selecting the same attribute
            let isNewAttributeSelected = (this.orderByAttribute !== newAttribIndex);

            // save undo step before change
            let addUndoStep = ((!attributeData.noUndo) && (attributeData.isRedo || isNewAttributeSelected));
            if( addUndoStep ){
                let attribNameForUndo = ((newAttribIndex >= 0) ? this.attribs[newAttribIndex].name : "Case"),
                    oldAttribID = this.getOrderByAttribute() ? this.getOrderByAttribute().id : undefined;
                Dyn.undoManager.saveUndoStep({
                    redoAttribID: newAttribID,
                    undoAttribID: oldAttribID,
                    actionID: "orderByAttributeByID",
                    actionString: Dyn.loc("Order by {{name}}", {"name": attribNameForUndo})
                }, this, attributeData.isRedo);
            }

            // make the change
            if( isNewAttributeSelected ){
                // save state
                this.orderByAttribute = newAttribIndex;
                // notify clients
                attributeData.attrib = this.getOrderByAttribute();
                mediator.publish("records:setOrderByAttribute", attributeData);
            }
            //Dyn.time( 'setOrderByAttribute', 'End' );
        }

        /**
         * Receive a "records:casesSelected" message from the Cards, Table, or Plot.
         * Cases are rows in the table, individual cards, or (usually) icons in the plot.
         *
         * @param caseData
         * @param {[]} caseData.ids --array of case indices, may be empty
         * @param {string} caseData.object --who sent the original message (Cards, Table, Plot)
         */
        setSelectedCaseByIndex(caseData={}) {
            let selectionChanged = false;
            Dyn.assert( this.selectedCases && caseData );

            if( caseData.toggleSelect && caseData.ids.length > 0 ) {
                // toggle the selection of the given case indices
                caseData.ids.forEach((newCaseIndex) => {
                    if( this.selectedCases.includes(newCaseIndex)) {
                        this.selectedCases = this.selectedCases.filter((ci) => ci !== newCaseIndex );
                    } else {
                        this.selectedCases.push( newCaseIndex );
                    }
                });
                selectionChanged = true;
            } else if( caseData.addToSelection && this.selectedCases.length > 0 ) {
                // add to existing cases, removing duplicates
                caseData.ids.forEach((newCaseIndex) => {
                    if( ! this.selectedCases.includes(newCaseIndex)) {
                        this.selectedCases.push(newCaseIndex);
                        selectionChanged = true;
                    }
                });
            } else {
                // accept the new list of case indices as the selection (or no selection if empty list)
                // for efficiency, first make sure that there is a change to the selection state
                caseData.ids.forEach((newCaseIndex) => {
                    if( ! this.selectedCases.includes(newCaseIndex)) {
                        selectionChanged = true; // a case will be added to selection
                    }
                });
                this.selectedCases.forEach((existingCaseIndex) => {
                    if( ! caseData.ids.includes(existingCaseIndex)){
                        selectionChanged = true; // a case will be removed from selection
                    }
                });
                // if changed, save the new array of selected case indices
                if( selectionChanged ){
                    this.selectedCases = caseData.ids;
                }
            }
            //console.log( "setSelectedCaseByIndex: "+this.selectedCases.length+" cases selected");

            // always clear categories or numeric range when selecting by case index
            caseData.isCategoryChange = (this.selectedCategories.length > 0);
            caseData.isNumericRangeChange = Boolean(this.selectedNumericRange);
            caseData.isMarqueeSelectUpdate = Boolean(caseData.isInDrag && ! caseData.isMarqueeSelectEnd);
            this.selectedCategories = [];
            this.selectedNumericRange = null;

            // inform other objects that use cases
            mediator.publish("records:preNotifyCasesSelected", caseData); // do before final notification
            if( selectionChanged ){
                mediator.publish("records:setCasesSelected", caseData);
            } else if( caseData.isMarqueeSelectEnd ) {
                // send an additional (optional) notification at the end of marquee selection
                // so that views that ignored isMarqueeSelectUpdate events can now update.
                mediator.publish("records:setCasesSelectedEnd", caseData);
            }
        }

        /**
         * Receive a "records:casesSelectedByCategory" message, and select the
         * cases that match the given attribute's category value.
         * @param selectData -- {attributeID, category} attribute name, category value to select.
         */
        setSelectedCaseByCategory(selectData={}) {
            Dyn.assert( this.selectedCases && this.selectedCategories && selectData );
            let attribute = this.getAttributeByID(selectData.attributeID),
                isFilteredOrMissingCaseFunc = this.getStatsFilterFunction(),
                category = selectData.category,
                selectionChanged = false,
                addToSelection = Boolean(selectData.addToSelection), // because user did shift-click
                isNumericInterval = Boolean(selectData.isNumericInterval), // special case of numeric interval category
                intervalIndex = -1;

            if( selectData.toggleSelect && this.selectedCategories.includes(category)) {
                // toggle de-select a category; just clear the selection
                attribute = null;
                this.setSelectedCategoryAttribute(null);
                this.selectedCategories = [];
                this.selectedCases = [];
                selectionChanged = true;
            }

            if( attribute ){
                // change or add a category to the list of selected categories
                this.setSelectedCategoryAttribute(attribute); // update the attribute that this category is for
                if( ! this.selectedCategories.includes(category)){
                    // category not already in list of selected categories, so add it now.
                    this.selectedCategories.push(category);
                    selectionChanged = true;
                }
                if( !addToSelection ){
                    // exclude previous categories from selection, if any
                    this.selectedCategories.forEach((value) => {
                        if( value !== category ){
                            selectionChanged = true; // at least one category will be removed from selection
                        }
                    });
                    this.selectedCategories = [category];
                }
                if( isNumericInterval && attribute.hasNumericIntervals() ){
                    // find index of numeric interval associated with this category, if any
                    intervalIndex = attribute.numericIntervals.categories.indexOf(category);
                    if( intervalIndex < 0 ){
                        // if interval index not valid, e.g. if category===Dyn.dataUtil.kMissingValue, then don't do interval checks on cases.
                        isNumericInterval = false;
                    }
                }
                // select only cases that match the category value
                if( selectionChanged ) {
                    let cases = attribute.cases;
                    if( !addToSelection ) {
                        this.selectedCases = []; // clear selection
                    }
                    for( let i = 0; i < cases.length; i++ ) {
                        if( isFilteredOrMissingCaseFunc && isFilteredOrMissingCaseFunc( i ) ) {
                            // skip this case, it is excluded from plot (filtered or missing)
                            continue;
                        }
                        if( isNumericInterval && (intervalIndex === Dyn.dataUtil.calcIntervalIndex(
                            attribute.numericIntervals.values, cases[i] )) ) {
                            // case value is within numeric interval
                            this.selectedCases.push( i );
                        } else if( cases[i] === category ) {
                            // case value matches category string or number exactly
                            this.selectedCases.push( i );
                        }
                    }
                }
            }

            if( selectionChanged ) {
                // inform other objects that use cases
                let caseData = {
                    ids: this.selectedCases,
                    object: selectData.object,
                    isCategoryChange: true,
                    isNumericRangeChange: Boolean(this.selectedNumericRange)
                };
                this.selectedNumericRange = null; // clear numeric range if any
                mediator.publish("records:preNotifyCasesSelected", caseData); // do before final notification
                mediator.publish("records:setCasesSelected", caseData);
                console.log("setSelectedCaseByCategory", selectData, caseData );
            }
            // else if no change we don't notify or redraw
        }

        /**
         * Receive a "records:casesSelectedByNumericRange" message, and select the
         * cases that are within the given numeric range of the given attribute.
         * @param selectData -- {attributeID, dataRange, addToSelection }
         */
        setSelectedCaseByNumericRange(selectData) {
            let attribute = this.getAttributeByID(selectData.attributeID),
                range = selectData && selectData.dataRange,
                oldRange = new Dyn.mathUtil.Range(
                    this.selectedNumericRange ? this.selectedNumericRange.min : NaN,
                    this.selectedNumericRange ? this.selectedNumericRange.max : NaN),
                selectionChanged = false,
                addToSelection = Boolean(selectData && selectData.addToSelection); // because user did shift-click

            // if update the range, and the attribute the range applies to, if either missing, clear the numeric range
            if( attribute && range && range.isFinite() ){
                this.setSelectedCategoryAttribute(attribute);
                if( addToSelection && this.selectedNumericRange ){
                    // expand the existing range
                    this.selectedNumericRange.addPoint(range.min);
                    this.selectedNumericRange.addPoint(range.max);
                } else {
                    // save new range
                    this.selectedNumericRange = new Dyn.mathUtil.Range(range.min, range.max);
                }
                selectionChanged = !this.selectedNumericRange.equals(oldRange);
            } else {
                // clear the range
                selectionChanged = Boolean(this.selectedNumericRange);
                this.selectedNumericRange = null;
                this.setSelectedCategoryAttribute(null);
            }

            // select only cases that match the category value
            if( selectionChanged ){
                let isFilteredOrMissingCaseFunc = this.getStatsFilterFunction(),
                    cases = attribute.cases,
                    caseData,
                    i, j;

                if( !addToSelection ){
                    this.selectedCases = []; // clear selection
                }
                for( i = 0, j = cases.length; i < j && this.selectedNumericRange; i++ ){
                    if( isFilteredOrMissingCaseFunc && isFilteredOrMissingCaseFunc(i) ){
                        // skip this case, it is excluded from plot (filtered or missing)
                        continue;
                    }
                    let value = cases[i];
                    if( this.selectedNumericRange.isInRange(value) ){
                        // case value is within numeric interval
                        this.selectedCases.push(i);
                    }
                }
                // inform other objects that use cases
                caseData = {
                    ids: this.selectedCases,
                    object: selectData.object,
                    numericRange: range,
                    isCategoryChange: this.selectedCategories.length > 0,
                    isNumericRangeChange: true
                };
                this.selectedCategories = []; // clear old category selection if any
                mediator.publish("records:preNotifyCasesSelected", caseData); // do before final notification
                mediator.publish("records:setCasesSelected", caseData);
            }
            // else if no change we don't notify or redraw
        }

        /**
         * [setSelectedCategoryAttribute description]
         * @param {[type]} attribute [description]
         * When attribute is filtered from the plot itself, by clicking on the axis
         * set the selected category's attribute details
         */
        setSelectedCategoryAttribute(attribute) {
            Dyn.dataSet.selectedCategoryAttribute = attribute;
        }

        /**
         * [getSelectedCategoryAttribute description]
         * @return {[type]} [description]
         * get selected category's attribute details
         */
        getSelectedCategoryAttribute() {
            return Dyn.dataSet.selectedCategoryAttribute;
        }

        /**
         * Get the filter function for our attribute stats, to calculate the statistics
         * only on cases that pass the filter and do not have missing values in the
         * current plot.
         *
         * @returns {*}
         */
        getStatsFilterFunction() {

            /**
             * @param caseIndex
             * @returns {Boolean} true if case did not pass the case filter or is marked as missing by plot
             */
            function isFilteredOrMissingCase(caseIndex) {
                var dataSet = Dyn.dataSet;
                return (dataSet.caseFilter.caseIsFiltered[caseIndex] || dataSet.missingCases.caseIsMissing[caseIndex]);
            }

            var filter = this.caseFilter,
                hasActiveFilter = filter && (filter.getNumCasesFiltered() > 0),
                missingCases = this.missingCases,
                hasMissingCases = missingCases && (missingCases.getNumCasesMissing() > 0);

            if( hasActiveFilter || hasMissingCases ){
                return isFilteredOrMissingCase;
            }
            return null; // no filtering needed
        }

        /**
         * Get the ID associated with the current filter,
         * so we can compare IDs to tell when the filter has changed.
         * @returns {number}
         */
        getStatsFilterID() {
            var id = 0, // default ID if caseFilter or missingCases not yet initialized
                initialized = this.caseFilter && this.missingCases;

            if( initialized ){
                // both IDs are based on an always incrementing serial number so it is safe to add them together
                // to create a unique combined ID.
                id = this.caseFilter.getID() + this.missingCases.getID();
            }
            return id;
        }

        /**
         * evaluateFormulas() - Evaluate all the attribute formulas in this dataSet in the correct order.
         * evaluateFormulas( Dyn.attribute ) - Evaluate the given attribute's formulas, and any other
         *      attributes that reference it in their formulas.
         * The evaluation will update the case data values in each attribute.
         * The caller should send notification to the Case Cards, Table, and Plot to update
         * @param [changedAttributes] -- optionally, array of attributes that have changed
         * @returns {[]} -- attribute IDs of changed attributes
         */
        evaluateFormulas( changedAttributes=null ) {
            var dataSet = this,
                startingAttributes = changedAttributes || this.attribs,
                isUpstreamSearch = changedAttributes ? true : false,
                attribsVisited = [],
                attribStack = [];
            Dyn.assert( Array.isArray(startingAttributes));

            // find downstream dependencies
            //      parses formula string for first time if needed
            //      finds attributes (formula or not) that each formula attribute references
            this.attribs.forEach(function(attr) {
                if( attr.formula ){
                    attr.formula.findDependencies( dataSet, attr );
                }
            });

            // find upstream dependencies
            //      finds the attributes that reference each formula (reverse of downstream dependencies)
            this.attribs.forEach(function(attr) {
                if( attr.formula ){
                    attr.formula.findReverseDependencies( dataSet, attr );
                }
            });

            // add formula attributes to attribStack array
            //      Use depth-first sort to get them in the proper order to be evaluated
            //      e.g. if a's formula references formula attribute b, and c references neither, order will be [b a c]
            //      attribsVisited is used to visit each attribute once when checking dependencies (avoid infinite loops)
            startingAttributes.forEach(function(attr) {
                if( attr.formula ){
                    attr.formula.addAttributeToStackEvaluateOrder(attribStack, attribsVisited, attr, dataSet, isUpstreamSearch);
                }
            });

            // evaluate formula attributes in order;
            //      also check for dependency loops: if a' formula references b, b refs c, and c refs a, then show error result.
            //console.log("Dyn.DataSet.evaluateFormulas()", attribStack);
            let idsOfUpdatedAttributes=[];
            attribStack.forEach(function(attr, index) {
                //console.log("Dyn.DataSet.evaluateFormulas() for "+attr.id );
                attr.formula.checkForDependencyLoops(attribStack.slice(index, attribStack.length), dataSet);
                attr.calcCasesFromFormula();
                idsOfUpdatedAttributes.push( attr.id );
            });

            // caller can notify the Plot and Case Table that attribute values have changed, so they can update
            // mediator.publish("records:setAttributeValues", { attributeIDs:idsOfUpdatedAttributes });
            return idsOfUpdatedAttributes;
        }

        /**
         * Given an attribute that has a case value changed, find attributes with formulas that
         * depend on that case value, so we know which attributes to re-evaluate.
         * @param idsOfChangedAttributes -- Array of Dyn.Attribute.id that have changed case values
         * @returns {*[]} -- array of attributes which have formulas needing re-eval.
         */
        findAttributesToEvaluate( idsOfChangedAttributes ) {
            let foundAttribs = [];
            idsOfChangedAttributes.forEach((attrID1) =>
                findDependentAttribsRecursive( this.attribs, foundAttribs, attrID1 ));
            return foundAttribs;

            function findDependentAttribsRecursive( allAttribs, foundAttribs, attrID1 ) {
                // if any of searchAttribs is dependent on attr1, add it to foundAttribs array.
                // recursive, and handles dependency loops
                // (e.g c depends on b depends on a depends on c => return [a,b,c])
                allAttribs.forEach(( attr2 ) => {
                    if( attr2.formula?.isDependentOn( attrID1 )
                        && ( ! foundAttribs.includes(attr2.id))) { // stop recursion loops
                        console.log("findAttributesToEvaluate", attr2.id+" formula references "+attrID1 );
                        foundAttribs.push( attr2 ); // attr2 has a formula needing re-eval
                        findDependentAttribsRecursive( allAttribs, foundAttribs, attr2.id );
                    }});
            }
        }

        /**
         * Get the SVG path string for the custom case icon saved with this dataset.
         * The path string is used to replace the circle icon in dot plots and similar plots.
         * @returns {null|string}
         */
        getCustomCaseIcon() {
            let pathString = null; // for testing use: "M150 0 L75 200 L225 200 Z";
            if( this.metaData &&
                Dyn.plotUtil.checkNestedProp(this.metaData, 'caseIcon', 'pathString') ){
                // for now look for explicit path string and no other attributes
                // And expect a closed path string with non-zero bbox
                // Possible future enhancement would be to allow other SVG attributes to be set
                let customCaseIcon = this.metaData.caseIcon;
                if( customCaseIcon.pathString &&
                    typeof customCaseIcon.pathString === "string" ){
                    pathString = customCaseIcon.pathString;
                }
            }
            // for testing
            return pathString;
        }

        /**
         * Get the size of the custom case icon saved with this dataset.
         * The size is used to update the case icon in dot plots and similar plots.
         * @returns {integer|0}
         */
        getCustomIconSize() {
            let iconSize = 0;
            if( this.metaData &&
                Dyn.plotUtil.checkNestedProp(this.metaData, 'caseIcon', 'size') ){
                iconSize = this.metaData.caseIcon.size;
            }
            return iconSize;
        }

        /**
         * get the attribute(s) to show in the case tooltip, by default.
         * @returns {[Dyn.Attribute]} -- array of 0+ attributes
         */
        getTooltipAttributes() {
            let tooltipAttributes = [];
            if( Array.isArray(this.metaData?.tooltipAttributeIDs)) {
                tooltipAttributes = this.metaData.tooltipAttributeIDs
                    .map( id => this.getAttributeByID(id))
                    .filter( attr => !!attr );
            }
            return tooltipAttributes;
        }

        /**
         * Update the number of cases of our attributes, by adding missing values to the end,
         * or removing attributes from the end.
         * @param newNumCases -- integer 0+
         * @returns {boolean}
         */
        setNumCases( newNumCases ) {
            let changed = false;
            this.attribs.forEach( a => {
                if( a.setNumCases(newNumCases) ){
                    changed = true;
                }
            });
            return changed;
        }

        /**
         * Return the number of cases the dataset would have if we removed any cases
         * that had all missing values, and did not have non-missing cases after them.
         * Ignores the "Case" and derived attributes, since these are not editable.
         * The result can be used as input to this.setNumCases()
         */
        calcTrimmedNumberOfCases( maxCasesToTrim ) {
            function canTrimCase( attributeArray, caseIndex ) {
                let hasNonMissingEditableData = attributeArray.find( a =>
                    a.isEditable() && ! Dyn.dataUtil.isMissing( a.originalCases[caseIndex] ));
                return hasNonMissingEditableData ? false : true;
            }
            let numCasesPreFilter = this.getNumCasesPreFilter(),
                numCasesChecked = 0;
            while( (numCasesPreFilter >= 1)
                && (numCasesChecked < maxCasesToTrim )
                && canTrimCase( this.attribs, numCasesPreFilter-1 ) ) {
                --numCasesPreFilter; // trim one case
                ++numCasesChecked;
            }
            return numCasesPreFilter;
        }

        /**
         * Return a list of empty attributes that can be deleted from the end of the dataset.
         * NOTE: when you delete an attribute name, it reverts to the default name,
         * so we must pass in a list of deleted names rather than test for an empty string.
         * @param attributeIndex -- index of attribute to start from (e.g. attribute that we are editing)
         * @param idsOfDeletedAttributeNames {[]} -- array of attribute ID strings that had the name deleted
         * @returns {[]} -- array of attributes to delete (Dyn.Attribute), 0+ entries
         */
        calcTrimmedAttributes( attributeIndex, idsOfDeletedAttributeNames ) {
            function canTrimAttribute( attributeArray, attributeIndex, lastTrimIndex ) {
                let lastAttributeIndex = Math.min( lastTrimIndex, attributeArray.length) - 1,
                    isLastAttribute = (attributeIndex === lastAttributeIndex );
                if( isLastAttribute ) {
                    let attr = attributeArray[attributeIndex],
                        isEditable = attr.isEditable(),
                        isEmptyOfValues = attr.statsPreFilter.isMissing(),
                        //isDefaultName = attr.name===Dyn.dataUtil.defaultAttributeName(attributeIndex),
                        nameWasErased = idsOfDeletedAttributeNames.includes( attr.id );
                    return Boolean( isLastAttribute && isEditable &&
                        isEmptyOfValues && nameWasErased );
                } else {
                    return false;
                }
            }
            let attributesToTrim = [],
                lastTrimIndex = this.attribs.length;
            while( attributeIndex >= 0 && canTrimAttribute( this.attribs, attributeIndex, lastTrimIndex )) {
                attributesToTrim.push( this.attribs[attributeIndex].id );
                lastTrimIndex = attributeIndex;
                --attributeIndex;
            }
            if( attributesToTrim.length ) console.log( "calcTrimmedAttributes", attributesToTrim );
            return attributesToTrim;
        }

        static unitTest( dataSet ) {
            if( Dyn.debugging ) {
                let unitTester = new Dyn.UnitTester( "Dyn.dataSet.unitTest()" );

                // test raw table copy with given dataSet
                let tableRaw = getRawTable( dataSet );
                testRawTable( unitTester, dataSet, tableRaw );

                // test again with a small fixed data set
                testTableCopy( unitTester );
                unitTester.report();
            }

            function getRawTable( dataSet ) {
                let rows = [];
                if( false /* slower way, don't use */) {
                    Dyn.time( "Dataset:getTableRowData", "Start" );
                    const caseValueFormat = { rawNumbers: true, formatted: false, noDerivedData: true }
                    for( let i = 0, numRows = dataSet.getNumCasesPreFilter(); i < numRows; i++ ) {
                        rows.push( dataSet.getTableRowData( i, caseValueFormat ) );
                    }
                    Dyn.time( "Dataset:getTableRowData", "End" );
                } else {
                    Dyn.time("Dataset:getTableRowDataRaw", "Start");
                    rows = Dyn.dataUtil.getTableRowDataRaw( dataSet );
                    Dyn.time("Dataset:getTableRowDataRaw", "End");
                }
                return rows;
            }
            function testRawTable( unitTester, dataSet, tableRaw ) {
                const colOffset = dataSet.attribs[0]?.isCaseAttribute ? 1 : 0,
                    rowOffset = -1;
                tableRaw.forEach( (row,rowIndex) => {
                    row.forEach( (d1, colIndex) => {
                        let d2 = (rowIndex===0) ? (dataSet.attribs[colIndex+colOffset]?.name)
                            : (dataSet.attribs[colIndex+colOffset]?.originalCases[rowIndex+rowOffset]);
                        unitTester.test( d2===d1
                            || (d1===null && dataSet.attribs[colIndex+colOffset].isDerived),
                            "getTableRowDataRaw() mismatch in "+rowIndex+","+colIndex );
                    });
                });
            }
            function testTableCopy(unitTester) {
                // test copyRecordListToColumnArrays(), copyColumnArraysToAttribs(), getTableRowDataRaw()
                const cases = [
                        ["a", 1, 1.1],
                        ["b", 2, 2.2],
                        ["*", 3, 3.3] ],
                    names = ["catAttrib", "numAttrib", "formAttrib"],
                    formulas = [null,null,"num2*1.1"],
                    metaData = makeTestMetaData( names, formulas ),
                    dataSet = makeTestDataSet( cases, names, metaData);
                // convert back to a table of raw data
                let table1 = [names].concat(cases),
                    table2 = Dyn.dataUtil.getTableRowDataRaw( dataSet );
                testRawTable( unitTester, dataSet, table2 );
            }
            function makeTestMetaData( names, formulas ) {
                let metaData = {
                    fields: []
                };
                names.forEach((name,i)=> {
                    metaData.fields.push( Dyn.MetaData.newMetaDataFieldFromName( i, name, name+"_long", formulas[i] ));
                });
                return metaData;
            }
            function makeTestDataSet( table, names, metaData ) {
                let ids = metaData.fields.map((f)=>f.id),
                    columns = Dyn.dataUtil.copyRecordListToColumnArrays( table, ids ),
                    attribs = Dyn.dataUtil.copyColumnArraysToAttribs( names, columns, ids, metaData ),
                    dataSet = {
                        attribs: attribs,
                        getNumCasesPreFilter() { return this.attribs[0].originalCases.length }
                    };
                return dataSet;
            }
        }
    } // class DataSet
    Dyn.DataSet = DataSet;

} // wrapDynDataSet

// Note: Dyn.CaseFilter moved to ../caseFilter.js on Nov-16-2020
// Note: Dyn.CaseSampler moved to ../caseSampler.js on Nov-16-2020
